/** Public check of a barangay document (FR-DOC-04): type, name, date issued and status only. */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BadgeCheck, ShieldAlert } from 'lucide-react';

interface Result { genuine: boolean; status?: string; valid?: boolean; reason?: string; document?: string; name?: string; issuedOn?: string; validUntil?: string | null; trackingNo?: string; barangay?: string }
const WHY: Record<string, string> = { CANCELLED: 'This document was cancelled by the barangay.', EXPIRED: 'This document has expired.', NOT_YET_RELEASED: 'This document was approved but not yet released.' };

export function VerifyDocPage({ kind = 'doc' }: { kind?: 'doc' | 'kp' }): JSX.Element {
  const { token } = useParams();
  const [r, setR] = useState<Result | null>(null);
  useEffect(() => { void fetch(`/api/v1/public/verify/${kind}/${encodeURIComponent(token ?? '')}`).then((x) => x.json()).then(setR).catch(() => setR({ genuine: false, reason: 'Could not check right now. Try again.' })); }, [token, kind]);
  const ok = r?.valid;
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-4 dark:bg-slate-950">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Barangay document check</p>
        {!r ? <p className="mt-6 text-slate-500">Checking…</p> : (
          <>
            {ok ? <BadgeCheck aria-hidden="true" className="mx-auto mt-4 h-12 w-12 text-emerald-600" /> : <ShieldAlert aria-hidden="true" className="mx-auto mt-4 h-12 w-12 text-red-600" />}
            <p className={`mt-2 text-lg font-bold ${ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>{!r.genuine ? 'Not a document of this system' : ok ? 'Genuine and valid' : 'Genuine, but not valid'}</p>
            {r.document && <p className="mt-3 font-semibold text-slate-900 dark:text-white">{r.document}</p>}
            {r.name && <p className="text-slate-700 dark:text-slate-200">{r.name}</p>}
            {r.barangay && <p className="text-sm text-slate-500">{r.barangay}</p>}
            {r.issuedOn && <p className="mt-2 text-sm text-slate-500">Issued {r.issuedOn}{r.validUntil ? ` · valid until ${r.validUntil}` : ''} · {r.trackingNo}</p>}
            {!ok && <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{r.reason ?? WHY[r.status ?? ''] ?? ''}</p>}
          </>
        )}
      </div>
    </main>
  );
}
